﻿import { BaseKb3dModel } from '../decorators';
import { Enumerable } from '../decorators/enumerable';
import { Connector } from '../features/connector';
import { Feature } from '../features/feature';
import { ISpaceNode } from '../kb3d-models';
import { Kb3dObject } from '../kb3d-object';
import { KbQuaternion } from '../primitives/kb-quaternion';
import { KbVector } from '../primitives/kb-vector';
import { Token } from '../token';

@BaseKb3dModel({
    token: Token.SpaceNode,
})
export abstract class SpaceNode extends Kb3dObject implements ISpaceNode {
    constructor() {
        super();
        // this._rotationQuaternion = nodeConfig.rotationQuaternion ? KbQuaternion.FromVec3(nodeConfig.rotationQuaternion) : KbQuaternion.Identity();
        // this._rotation = this._rotationQuaternion.toEuler();
    }
    visible: boolean;
    position: KbVector;
    useEuler: boolean;
    eulerRotation: KbVector;
    rotationAngle: number;
    rotationAxis: KbVector;
    scaling: number;
    pivot: KbVector;
    fixed: boolean;
    _primeMover: boolean;
    /** override base parent for more specific typing*/
    _parent: SpaceNode | undefined;
    _showPivot = false;

    preTransform?: number[];

    getRotationQuaternion() {
        if (this.useEuler && this.eulerRotation) {
            return KbQuaternion.FromYawPitchRoll(this.eulerRotation);
        } else if (this.rotationAxis && this.rotationAngle !== undefined) {
            return KbQuaternion.FromRotationAxis(this.rotationAxis, this.rotationAngle);
        }
        return KbQuaternion.Identity();
    }
    setRotationQuaternion(q: KbQuaternion) {
        if (this.useEuler) {
            this.eulerRotation = q.toEuler();
        } else {
            this.rotationAngle = q.getAngle();
            this.rotationAxis = q.getAxis();
        }
    }

    private _features: Feature[];
    @Enumerable()
    public get features(): Feature[] {
        if (!this._features) this._features = [];
        return this._features;
    }
    public set features(val) {
        this._features = val;
    }

    private _connectors: Connector[];
    @Enumerable()
    public get connectors(): Connector[] {
        if (!this._connectors) this._connectors = [];
        return this._connectors;
    }
    public set connectors(val) {
        this._connectors = val;
    }

    public getChildren(): Kb3dObject[] {
        return [...this.features, ...this.connectors];
    }

    public getChildArrayForType(token: Token): Kb3dObject[] | undefined {
        if (token.isFeature()) {
            return this.features;
        } else if (token == Token.Connector) {
            return this.connectors;
        } else {
            throw Error('unexpected token');
        }
    }

    /** overriden by derived classes to get only children that derive from SpaceNode (like groups, meshes, nested scenes) */
    public abstract getSpaceChildren(): SpaceNode[];

    public getAncestors(): SpaceNode[] {
        return super.getAncestors() as SpaceNode[];
    }

    public copyPropertiesTo(target: SpaceNode) {
        target.name = this.name;
        target.visible = this.visible;
        target.position = this.position;
        target.useEuler = this.useEuler;
        target.eulerRotation = this.eulerRotation;
        target.rotationAngle = this.rotationAngle;
        target.rotationAxis = this.rotationAxis;
        target.scaling = this.scaling;
        target.pivot = this.pivot;
        target.fixed = this.fixed;
        target._primeMover = this._primeMover;
        target._showPivot = this._showPivot;
        target.preTransform = this.preTransform;
    }

    public getFeatureByName(name: string) {
        return [...this.features, ...this.connectors].find(f => f.name.isEqual(name));
    }

    public rotate(axis: KbVector, angle: number) {
        axis.normalize();

        const q = KbQuaternion.FromRotationAxis(axis, angle);
        this.setRotationQuaternion(this.getRotationQuaternion().multiply(q));
    }
}
